import { FC, useMemo, useState } from 'react'
import { Box, CircularProgress, Typography } from '@mui/material'
import theme from 'theme'
import SearchInput from 'ui/components/form-components/SearchInput'
import { JiraFilterPreview } from 'models'
import PreviewEmptyState from '../../components/PreviewEmptyState'
import { PreviewPaneWrapper } from '../dataConfiguration.styles'
import FilterPreviewTable from './FilterPreviewTable'

const EMPTY_TITLE = 'No Preview Available'
const EMPTY_SUBTITLE = 'Add at least one data filter to preview project sample'
const NO_MATCHES_NOTE = 'No issues match your search.'
const HAS_MORE_NOTE = 'More issues match these filters — only the first page is shown.'

/* The design's search field: 280px, the shared input's 48px height and 12px
 * radius, with the design's border and label ramp. */
const SEARCH_SX = {
  width: '280px',
  marginBottom: `${theme.spacing.half}px`,
  '& .MuiOutlinedInput-input': {
    borderColor: theme.colors.dropdownBorder,
    fontSize: theme.typography.fontSize.large,
    fontWeight: theme.typography.fontWeight.bold,
    lineHeight: '20px',
    letterSpacing: '0.1px'
  }
}

interface Props {
  preview?: JiraFilterPreview | null
  loading?: boolean
  error?: string
}

/* A sample of the issues the drafted filters match: search, the row count, and
 * one page of results. */
const FilterPreviewPane: FC<Props> = ({ preview, loading, error }) => {
  const [search, setSearch] = useState('')

  const issues = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term || !preview) return preview?.issues ?? []

    return preview.issues.filter((issue) =>
      [issue.issue_type, issue.key, issue.summary, issue.priority, issue.status, issue.assignee].some((value) =>
        (value ?? '').toLowerCase().includes(term)
      )
    )
  }, [preview, search])

  if (loading && !preview) {
    return (
      <PreviewPaneWrapper>
        <Box className="preview-placeholder">
          <CircularProgress size={40} />
        </Box>
      </PreviewPaneWrapper>
    )
  }

  if (error) {
    return <PreviewEmptyState title="Preview Failed" description={error} />
  }

  if (!preview) {
    return <PreviewEmptyState title={EMPTY_TITLE} description={EMPTY_SUBTITLE} />
  }

  return (
    <PreviewPaneWrapper>
      <SearchInput search={search} setSearch={setSearch} placeholder="Search" sx={SEARCH_SX} />

      {/* The full match count from Jira, not the sampled page size; a search
       * narrows the visible sample, so its count takes over. */}
      <Typography className="preview-row-count">
        Number of rows: <span>{search.trim() ? issues.length : preview.total ?? issues.length}</span>
      </Typography>

      {issues.length ? (
        <FilterPreviewTable issues={issues} search={search} />
      ) : (
        <Typography className="section-hint">{NO_MATCHES_NOTE}</Typography>
      )}

      {preview.has_more && <Typography className="section-hint">{HAS_MORE_NOTE}</Typography>}
    </PreviewPaneWrapper>
  )
}

export default FilterPreviewPane
